import React, { useCallback, useMemo, useState } from 'react';
import { ClipboardDocumentIcon, ArrowDownTrayIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from './ui/Drawer';
import { downloadEdiFile, type EdiPreviewFile } from '../services/pendingEdiInvoices.service';

/**
 * The built EDI invoice file, on screen.
 *
 * WHY THIS EXISTS RATHER THAN JUST A DOWNLOAD. macOS quarantines a downloaded .csv and
 * refuses to open it in TextEdit — "Apple could not verify this file is free of malware"
 * — so the one thing the operator wants to do with a preview, look at it, is the thing
 * the download makes hardest. Reading it here needs no round trip through Finder.
 *
 * Line numbers are not decoration: the point of a preview is comparing it against what
 * V1 produced, and "line 214 differs" is the sentence that comparison is made of.
 *
 * The body is rendered in a <pre> with its own horizontal scroll. Record 1 rows run to
 * ~310 characters and must NOT wrap — a wrapped line makes the column count impossible
 * to follow, which is the main thing being checked.
 *
 * TWO FORMATS ARRIVE HERE. A Kleinschmidt file is a CSV with real newlines. An internal
 * partner's X12 210 has NO newlines at all — the whole interchange is one line ending
 * in `~` per segment — so it is split on the terminator FOR DISPLAY ONLY. Copy and
 * Download still hand over the exact bytes: inserting newlines into an X12 file would
 * change what the partner receives.
 */
/**
 * One display line per record. A file with newlines is already line-oriented; one
 * without is X12, whose segments end in `~` and which would otherwise render as a single
 * unreadable line thousands of characters wide. The terminator is kept on the line so
 * what is on screen still reads as the file.
 */
export function splitForDisplay(body: string): string[] {
  if (!body) return [];
  if (body.includes('\n')) return body.split('\n');
  if (!body.includes('~')) return [body];
  return body.split('~').filter((s) => s !== '').map((s) => `${s}~`);
}

export interface EdiInvoiceFileViewerProps {
  open: boolean;
  onClose: () => void;
  bucketLabel: string;
  files: EdiPreviewFile[];
  /** Invoices validation held back, so the viewer can say why a count looks short. */
  blockedCount?: number;
  unmappedLineItems?: string[];
}

const EdiInvoiceFileViewer: React.FC<EdiInvoiceFileViewerProps> = ({
  open, onClose, bucketLabel, files, blockedCount = 0, unmappedLineItems = [],
}) => {
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const file = files[Math.min(active, Math.max(files.length - 1, 0))];

  const lines = useMemo(() => splitForDisplay(file?.body ?? ''), [file]);

  const copy = useCallback(async () => {
    if (!file) return;
    try {
      await navigator.clipboard.writeText(file.body);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard is blocked in some contexts; Download is the fallback and is right there.
    }
  }, [file]);

  if (!file) return null;

  return (
    <Drawer
      isOpen={open}
      onClose={onClose}
      position="right"
      width="min(72rem, 95vw)"
      title={
        <span className="flex items-baseline gap-2">
          <span>{bucketLabel}</span>
          <span className="font-mono text-xs text-muted">{file.filename}</span>
        </span>
      }
      footer={
        <div className="flex items-center justify-between gap-3 w-full">
          <span className="text-xs text-muted">
            {file.invoiceCount} invoice{file.invoiceCount === 1 ? '' : 's'} ·{' '}
            {lines.length} lines · {file.body.length.toLocaleString()} bytes
            {' · nothing has been sent or recorded'}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 rounded border border-gray-300 dark:border-gray-600 px-3 py-1 text-sm text-label hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              {copied ? <CheckIcon className="h-4 w-4" /> : <ClipboardDocumentIcon className="h-4 w-4" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
            <button
              type="button"
              onClick={() => void downloadEdiFile(file)}
              className="inline-flex items-center gap-1.5 rounded border border-gray-300 dark:border-gray-600 px-3 py-1 text-sm text-label hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        {(blockedCount > 0 || unmappedLineItems.length > 0) && (
          <div className="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
            {blockedCount > 0 && (
              <div>
                {blockedCount} invoice{blockedCount === 1 ? '' : 's'} held back by a
                blocking error and not in this file.
              </div>
            )}
            {unmappedLineItems.length > 0 && (
              <div>
                Held back — no charge code for this partner: {unmappedLineItems.join(', ')}
              </div>
            )}
          </div>
        )}

        {/* A partner with a batch count produces several files; each is sent separately. */}
        {files.length > 1 && (
          <div className="flex flex-wrap gap-1">
            {files.map((f, i) => (
              <button
                key={f.filename}
                type="button"
                onClick={() => setActive(i)}
                className={`rounded px-2 py-1 text-xs font-mono border ${
                  i === active
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200'
                    : 'border-gray-300 dark:border-gray-600 text-muted hover:bg-gray-50 dark:hover:bg-gray-800'
                }`}
              >
                File {i + 1} · {f.invoiceCount}
              </button>
            ))}
          </div>
        )}

        <div className="overflow-auto rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 max-h-[70vh]">
          <pre className="text-xs leading-5 font-mono p-0 m-0">
            {lines.map((line, i) => (
              <div key={i} className="flex hover:bg-blue-50 dark:hover:bg-blue-900/20">
                <span className="select-none sticky left-0 shrink-0 w-12 px-2 text-right text-muted bg-gray-100 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 tabular-nums">
                  {i + 1}
                </span>
                <span className="px-2 whitespace-pre text-label">{line}</span>
              </div>
            ))}
          </pre>
        </div>
      </div>
    </Drawer>
  );
};

export default EdiInvoiceFileViewer;
